/**
 * Gradebook API (issue #13) — grade items, the score grid, final submission,
 * overrides, the letter/GPA scale, and the student-facing `/me` views.
 *
 * Typed wrappers over the shared `api` client, mirroring courses.ts's shape.
 */
import { api } from "@/lib/api/client";
import { commitOptions, retryOptions, type BulkResult } from "@/lib/api/commitSeams";
import { withVersion } from "@/lib/api/conflict";

export interface RegisterWrite { key: string; version: string; retry?: boolean }
const options = (write?: RegisterWrite) => write ? (write.retry ? retryOptions(write.key) : commitOptions(write.key)) : {};
const versioned = <T extends object>(body: T, write?: RegisterWrite) => write ? withVersion(body, write.version) : body;

export type GradeItemType =
  | "assignment"
  | "quiz"
  | "midterm"
  | "final"
  | "participation"
  | "oral_evaluation";

export interface GradeItem {
  id: string;
  course_id: string;
  key: string | null;
  label: string;
  type: GradeItemType;
  weight: number;
  sort_order: number;
  updated_at?: string;
  /** The course basis made by creation, carried by that command's receipt. */
  course_updated_at?: string;
}

export interface CreateGradeItem {
  key?: string | null;
  label: string;
  type: GradeItemType;
  weight: number;
  sort_order?: number;
}

export type PatchGradeItem = Partial<CreateGradeItem>;

/**
 * One line's place in a renumbering. `expected_sort_order` is the position the
 * caller was looking at; the server refuses the whole write if the set has
 * moved since, so a reorder never lands on top of someone else's.
 */
export interface ReorderGradeItem {
  id: string;
  expected_sort_order: number;
  sort_order: number;
}

/** The exact released terminal event contributing a grade-line piece, never the newest unjudged attempt. */
export interface GradeContribution {
  assignment_id: string;
  assignment_title: string;
  submission_id: string;
  attempt_number: number;
  assignment_version_id: string;
  feedback_id: string;
  feedback_event_no: number;
  outcome: "graded" | "complete" | "missing";
  score: number | null;
  points: number | null;
  percent: number | null;
  selected_scale_row_id: string | null;
}

export interface GradeRow {
  contributions?: Record<string, GradeContribution[]>;
  enrollment_id: string;
  student_id: string;
  display_name: string;
  participation_kind: "credit" | "audit";
  final_locked?: boolean;
  certified?: boolean;
  scores: Record<string, number | null>;
  course_pct: number | null;
  letter: string;
}

/** Work whose judgements feed a line; a line with any takes no direct score. */
export interface LinkedAssignment {
  id: string;
  title: string;
}

export interface GradesGrid {
  updated_at?: string;
  items: GradeItem[];
  /** The lines as the grid read them, each with the work that feeds it. */
  grade_items?: (GradeItem & { linked: boolean; linked_assignments: LinkedAssignment[] })[];
  rows: GradeRow[];
  /** True if any enrollment in the roster has final_locked=true. */
  locked: boolean;
}

export interface ScoreUpsert {
  enrollment_id: string;
  grade_item_id: string;
  score: number | null;
}

export interface SubmitFinalResult {
  bulk?: BulkResult;
  submitted: number;
  requested: number;
  proposed: number;
}

export interface OverrideGradeBody {
  enrollment_id: string;
  final_score: number;
  final_grade_letter: string;
  override_reason: string;
}

export interface OverrideGradeResult {
  enrollment_id: string;
  final_score: number;
  final_grade_letter: string;
  override_reason: string;
  override_by: string;
  override_at: string;
}

export interface MyGradesCourse {
  course_id: string;
  course_code: string;
  course_title: string;
}

/** Coursework recorded as feeding a learner's line, as the learner may see it (#1393 contract 2). */
export interface RelatedWork {
  assignment_id: string;
  title: string;
}

export interface ProvisionalResultItem {
  id: string;
  label: string;
  weight: number;
  sort_order: number;
  score: number | null;
  /** Outside the basis: never part of the result's identity. Absent from a server older than #1393 M3. */
  related_work?: RelatedWork[];
}

export interface CurrentProvisionalResult {
  course_id: string;
  course_code: string;
  course_title: string;
  items: ProvisionalResultItem[];
  percentage: number | null;
  letter: string;
  advisory: boolean;
}

export type ProvisionalResultRead =
  | {
      status: "current";
      basis_id: string;
      provisional: true;
      result: CurrentProvisionalResult;
    }
  | {
      status: "stale";
      basis_id: string;
      support_url: string;
    }
  | {
      status: "unavailable" | "inconsistent";
      support_url: string;
    };

export interface MyGrades {
  courses: MyGradesCourse[];
}

export type TermStatus = "active" | "upcoming" | "closed";

export interface MyGpaTermCourse {
  code: string;
  letter: string | null;
  credits: number;
}

export interface MyGpaTerm {
  semester: string;
  status: TermStatus;
  provisional: boolean;
  courses: MyGpaTermCourse[];
}

export interface MyGpa {
  cumulative: number | null;
  terms: MyGpaTerm[];
}

export const gradebookApi = {
  getRanks: (courseId: string) => api.get<{ state: string; value?: { candidates: { id: string; rank: number | null; eligible: boolean; blocked: boolean; read: string }[] } }>(`/courses/${courseId}/register-ranks`),
  getGradeItems: (courseId: string) => api.get<GradeItem[]>(`/courses/${courseId}/grade-items`),
  createGradeItem: (courseId: string, body: CreateGradeItem, write?: RegisterWrite) =>
    api.post<GradeItem>(`/courses/${courseId}/grade-items`, versioned(body, write), options(write)),
  updateGradeItem: (courseId: string, itemId: string, body: PatchGradeItem, write?: RegisterWrite) =>
    api.patch<GradeItem>(`/courses/${courseId}/grade-items/${itemId}`, versioned(body, write), options(write)),
  reorderGradeItems: (courseId: string, items: ReorderGradeItem[], write?: RegisterWrite) =>
    api.put<GradeItem[]>(`/courses/${courseId}/grade-items/order`, versioned({ items }, write), options(write)),
  deleteGradeItem: (courseId: string, itemId: string, write?: RegisterWrite) =>
    api.delete<void>(`/courses/${courseId}/grade-items/${itemId}`, versioned({}, write), options(write)),

  getGrades: (courseId: string) => api.get<GradesGrid>(`/courses/${courseId}/grades`),
  postGrades: (courseId: string, scores: ScoreUpsert[], write?: RegisterWrite) =>
    api.post<{ updated: number; updated_at?: string }>(`/courses/${courseId}/grades`, versioned({ scores }, write), options(write)),
  submitFinal: (courseId: string, write?: RegisterWrite, ids?: string[]) => api.post<SubmitFinalResult>(`/courses/${courseId}/submit-final`, versioned({ enrollment_ids: ids }, write), options(write)),
  overrideGrade: (courseId: string, body: OverrideGradeBody, write?: RegisterWrite) =>
    api.post<OverrideGradeResult>(`/courses/${courseId}/grades/override`, versioned(body, write), options(write)),


  getMyGrades: () => api.get<MyGrades>("/me/grades"),
  getMyProvisionalResult: (courseId: string, basisId?: string) => {
    const query = basisId ? `?basis_id=${encodeURIComponent(basisId)}` : "";
    return api.get<ProvisionalResultRead>(`/me/provisional-results/${courseId}${query}`);
  },
  getMyGpa: () => api.get<MyGpa>("/me/gpa"),
};
